import { FormLabel, Grid } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import Switch, { SwitchProps } from "@mui/material/Switch";
import chroma from "chroma-js";
import { FieldMetaProps } from "formik";
import { ChangeEvent, FocusEvent, useMemo } from "react";
import { FieldError } from "./FieldError";
import { OCFormControl } from "./FormControl";

const OCSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 40,
  height: 20,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "100ms",
    "&.Mui-checked": {
      transform: "translateX(20px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.success.main,
        border: `1px solid ${chroma(theme.palette.success.light).brighten(1).css()}`,
        opacity: 1,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
      "& .MuiSwitch-thumb": {
        boxShadow: `${alpha(theme.palette.success.main, 0.25)} 0 0 0 0.2rem`,
        border: `1px solid ${chroma(theme.palette.success.light).brighten(1).css()}`,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 16,
    height: 16,
    borderRadius: 8,
    color: theme.palette.common.white,
    border: `1px solid ${theme.custom.forms.pristineColor}`,
    boxShadow: `${alpha(theme.custom.forms.labels, 0.25)} 0 0 0 0.2rem`,
  },
  "& .MuiSwitch-track": {
    borderRadius: 8,
    backgroundColor: theme.custom.forms.labels,
    border: `1px solid ${chroma(theme.custom.forms.labels).brighten(0.5).css()}`,
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 300,
    }),
  },
}));

export interface ISwitchFieldProps {
  input?: any;
  custom?: any;
  name?: string;
  labelSX?: any;
  value?: boolean | null;
  onFocus?: (e: FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  className?: string;
  switchlabels?: string[];
  switchtooltips?: string[];
  disabled?: boolean;
  meta?: Partial<FieldMetaProps<any>>;
  showSpinner?: boolean;
  isBoolean?: boolean;
}

// SWITCHFIELD COMPONENT
export const SwitchField = (props: ISwitchFieldProps): JSX.Element => {
  const { name, label, onFocus, onBlur, value, onChange, meta, labelSX, disabled, isBoolean } = props;

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (onChange && typeof onChange === "function") {
      e.target.value = "" + e.target.checked;
      onChange(e);
    }
  };

  const handleFocus = (e: FocusEvent<HTMLButtonElement>): void => {
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLButtonElement>): void => {
    if (onBlur && typeof onBlur === "function") {
      onBlur(e);
    }
  };

  const hasError = meta?.error ? true : false;
  return (
    <OCFormControl error={hasError} sx={{ width: "100%", my: 0.3 }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: hasError ? "space-between" : "flex-end",
          }}
          xs
        >
          <FieldError meta={meta} icon={true} />
          {label && <FormLabel sx={{ ...labelSX }}>{label}</FormLabel>}
        </Grid>
        <Grid item xs={7}>
          <OCSwitch
            value={value}
            checked={value}
            name={name}
            disabled={disabled ?? false}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
